import React from 'react';
import {
  DatePicker,
  DateTimePicker,
  Dropdown,
  Input,
  Loader,
  MultiSelectDropdown,
} from 'freemium-ui';
import { get } from 'lodash';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  AccountInfoType,
  AccountUserType,
  AccountUsersResponseType,
  ConditionType,
  ContactListType,
  FieldChoicesType,
  FieldOptionsType,
  FilterOperationType,
  FormServFieldType,
  UserType,
} from '../../../shared/types';
import {
  FILTER_DATE_TIME_VALUE_FIELDS,
  FILTER_DATE_VALUE_FIELDS,
  FILTER_INPUT_VALUE_FIELDS,
  FILTER_MULTIPLE_VALUE_FIELDS,
  FREEMIUM_INPUT_DATE_TIME_TYPES,
  FREEMIUM_INPUT_DATE_TYPES,
  FREEMIUM_INPUT_NUMBER_TYPES,
  FilterOperations,
  GLOBAL_DATE_FORMAT,
} from '../../../shared/constants';
import { Center } from '../../../shared/styles';
import {
  getAccountTimeFormat,
  getFilterValueTypeFromFieldType,
  getHtmlTypeFromFieldType,
} from '../../../shared/utils';
import { useGetContactLists } from '../../screens/lists/hooks';
import { useGetAllUsers } from '../../screens/settings/screens/user/hooks';
import { getInputValue, setInputValue } from '../../utils';

type FilterOperationProps = {
  onChange: (value: ConditionType) => void;
  field: FormServFieldType;
  value: ConditionType;
  errors: FieldErrors;
  disabled?: boolean;
  showError: boolean;
  userInfo?: UserType;
};

const EVALUATE_ON = 'CONTACT';

const getPlaceHolder = (type?: number) => {
  switch (type) {
    case 1:
      return 'forms.placeholders.enter_text_to_filter';
    case 7:
    case 17:
    case 19:
      return 'forms.placeholders.enter_date_to_filter';
    case 8:
      return 'forms.placeholders.enter_number_to_filter';
    case 18:
      return 'forms.placeholders.select_any_choice';
    default:
      return '';
  }
};

const getSelectedItem = (
  ids: string[] = [],
  valueArray: FieldChoicesType[] = [],
  idPath: string,
) => {
  const allValues = {};
  valueArray.forEach((val) => {
    allValues[val[idPath]] = val;
  });
  return (ids || []).map((i) => allValues[i]).filter((i) => !!i);
};

const FilterOperation = (props: FilterOperationProps) => {
  const {
    value,
    onChange,
    field,
    errors,
    showError,
    disabled = false,
    userInfo,
  } = props;
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const { data: contactListData = {}, isFetching: isLoadingContactLists } =
    useGetContactLists();
  const { contactList: contactLists = [] } = contactListData;
  const { data, isFetching: isLoadingAllUsers } = useGetAllUsers({
    enabled: ['creater_id', 'updater_id'].includes(field.name),
  });
  const { account_users = [] }: AccountUsersResponseType = data || {
    account_users: [],
  };
  const accountInfo: AccountInfoType =
    queryClient.getQueryData('accountinfo') || {};

  const { id, type, name, field_options = {}, choices: c = [] } = field;
  const choices: FieldChoicesType[] =
    contactLists && contactLists.length > 0 && name === 'list_ids'
      ? contactLists.map(({ display_id, label }: ContactListType) => ({
          id: `${display_id || ''}`,
          value: `${label || ''}`,
        }))
      : c?.map((eC) => {
          return {
            ...eC,
            value: i18n.exists(
              `contacts.import_contact.form_choices.${name}.${eC.id}`,
            )
              ? `${t(`contacts.import_contact.form_choices.${name}.${eC.id}`)}`
              : `${eC.value}`,
          };
        }) || [];

  const {
    option_label_path: olp = 'value',
    option_value_path: ovp = 'id',
  }: FieldOptionsType = field_options || {};
  const option_label_path = olp || 'value';
  const option_value_path = ovp || 'id';
  const filterOperation: FilterOperationType | undefined = value?.operator
    ? FilterOperations[value.operator]
    : undefined;

  React.useEffect(() => {
    // Remove the multi select choices if it was deleted
    const allChoiceIds = (choices || []).map((c) => c[option_value_path]);
    if (
      FILTER_MULTIPLE_VALUE_FIELDS.includes(
        filterOperation?.inputValueType || 0,
      ) &&
      !(value?.value || []).every((v) => allChoiceIds.includes(v))
    ) {
      onChange({
        ...value,
        name,
        evaluate_on: EVALUATE_ON,
        value: setInputValue(
          filterOperation?.inputValueType,
          (value.value || []).filter((v) => allChoiceIds.includes(v)),
          accountInfo,
        ),
      });
    }
  }, [value]);

  if (
    filterOperation &&
    filterOperation?.needValue &&
    ((isLoadingContactLists && name === 'list_ids') ||
      (isLoadingAllUsers && (name === 'updater_id' || name === 'creater_id')))
  )
    return (
      <Center>
        <Loader />
      </Center>
    );

  if (
    filterOperation &&
    filterOperation?.needValue &&
    (name === 'creater_id' || name === 'updater_id')
  ) {
    const selectedItem = value?.value
      ? account_users.find((u) => u.freshid_user_id === value.value) || null
      : null;
    return (
      <div className="filter-value">
        <Dropdown
          id={id}
          disabled={disabled}
          name={name}
          emptyItemsText={`${t('no_results_found')}`}
          isRequired={filterOperation?.needValue}
          placeholder={`${t('segments.choose_an_user')}`}
          itemToString={(u: AccountUserType) =>
            `${get(u, ['user', 'first_name'], '') || ''} ${
              get(u, ['user', 'middle_name'], '') || ''
            } ${get(u, ['user', 'last_name'], '') || ''}`.trim() ||
            get(u, ['user', 'email'], '') ||
            ''
          }
          defaultSelectedItem={selectedItem}
          onChange={(option: AccountUserType) => {
            if (!option || !option.freshid_user_id) return;
            onChange({
              ...value,
              name,
              evaluate_on: EVALUATE_ON,
              value: option.freshid_user_id,
            });
          }}
          items={account_users}
        />
      </div>
    );
  }

  return filterOperation && filterOperation?.needValue ? (
    <div className="filter-value">
      {filterOperation?.inputValueType && !filterOperation?.between && (
        <>
          {/* inputValueType determines what field to be shown to get value */}
          {FILTER_INPUT_VALUE_FIELDS.includes(
            filterOperation?.inputValueType,
          ) && (
            <Input
              id={`${id}-value-input-value`}
              name={`${name}-input`}
              disabled={disabled}
              autoFocus
              type={getFilterValueTypeFromFieldType(
                filterOperation?.inputValueType,
              )}
              placeholder={`${t(
                getPlaceHolder(filterOperation?.inputValueType),
              )}`}
              isRequired={filterOperation?.needValue}
              value={
                getInputValue(
                  filterOperation?.inputValueType,
                  value.value,
                  accountInfo,
                ) || ''
              }
              errorText={get(errors, ['message'], '')}
              isErrored={showError && get(errors, ['message'], false)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onChange({
                  ...value,
                  name,
                  evaluate_on: EVALUATE_ON,
                  value: setInputValue(
                    filterOperation?.inputValueType,
                    e.target.value,
                    accountInfo,
                  ),
                });
              }}
            />
          )}
          {FILTER_MULTIPLE_VALUE_FIELDS.includes(
            filterOperation?.inputValueType,
          ) && (
            <MultiSelectDropdown
              items={choices}
              disabled={disabled}
              id={`${id}-multi-value`}
              name={`${name}-multi-input`}
              placeholder={`${t(getPlaceHolder(type))}`}
              isRequired={filterOperation?.needValue}
              selectedItem={getSelectedItem(
                value?.value || [],
                choices || [],
                option_value_path,
              )}
              maxAllowedLength={10}
              onChange={(val: FieldChoicesType[]) => {
                onChange({
                  ...value,
                  name,
                  evaluate_on: EVALUATE_ON,
                  value: setInputValue(
                    filterOperation?.inputValueType,
                    val.map((v: any) => v[option_value_path]),
                    accountInfo,
                  ),
                });
              }}
              errorText={get(errors, ['message'], '')}
              isErrored={showError && get(errors, ['message'], false)}
              itemLabelIdentifier={option_label_path}
              itemValueIdentifier={option_value_path}
            />
          )}
          {FILTER_DATE_TIME_VALUE_FIELDS.includes(
            filterOperation?.inputValueType,
          ) && (
            <DateTimePicker
              id={`${id}-value`}
              name={`${name}-input`}
              disabled={disabled}
              placeholder={`${t(getPlaceHolder(type))}`}
              displayFormat={getAccountTimeFormat(accountInfo)}
              errorText={get(errors, ['message'], '')}
              isErrored={
                showError &&
                ((filterOperation?.needValue && !value.value) ||
                  get(errors, ['message'], false))
              }
              isRequired={filterOperation?.needValue}
              value={getInputValue(type, value.value, accountInfo)}
              onChange={(date: Date) => {
                onChange({
                  ...value,
                  name,
                  evaluate_on: EVALUATE_ON,
                  value: setInputValue(type, date, accountInfo),
                });
              }}
            />
          )}
          {FILTER_DATE_VALUE_FIELDS.includes(
            filterOperation?.inputValueType,
          ) && (
            <DatePicker
              id={`${id}-value`}
              name={`${name}-input`}
              disabled={disabled}
              placeholder={`${t(getPlaceHolder(type))}`}
              errorText={get(errors, ['message'], '')}
              isErrored={showError && get(errors, ['message'], false)}
              isRequired={filterOperation?.needValue}
              displayFormat={GLOBAL_DATE_FORMAT}
              value={getInputValue(
                filterOperation.overrideFieldType
                  ? filterOperation.inputValueType || type
                  : type,
                value.value,
                accountInfo,
              )}
              onChange={(date: Date) => {
                onChange({
                  ...value,
                  name,
                  evaluate_on: EVALUATE_ON,
                  value: setInputValue(
                    filterOperation.overrideFieldType
                      ? filterOperation.inputValueType || type
                      : type,
                    date,
                    accountInfo,
                  ),
                });
              }}
            />
          )}
        </>
      )}
      {filterOperation?.between && (
        <>
          <>
            {FREEMIUM_INPUT_DATE_TYPES.includes(type) && (
              <DatePicker
                id={`${id}-value`}
                name={`${name}-input`}
                disabled={disabled}
                placeholder={`${t(getPlaceHolder(type))}`}
                isRequired={filterOperation?.needValue}
                errorText={get(errors, ['from', 'message'], '')}
                isErrored={
                  showError &&
                  (get(errors, ['message'], false) ||
                    get(errors, ['from', 'message'], false))
                }
                value={getInputValue(
                  type,
                  get(value, ['value', 'from'], ''),
                  accountInfo,
                )}
                onChange={(date: Date) => {
                  onChange({
                    ...value,
                    name,
                    evaluate_on: EVALUATE_ON,
                    value:
                      typeof value.value === 'object'
                        ? {
                            ...value.value,
                            from: setInputValue(type, date, accountInfo),
                          }
                        : {
                            from: setInputValue(type, date, accountInfo),
                          },
                  });
                }}
              />
            )}
            {FREEMIUM_INPUT_DATE_TIME_TYPES.includes(type) && (
              <DateTimePicker
                id={`${id}-value`}
                name={`${name}-input`}
                disabled={disabled}
                placeholder={`${t(getPlaceHolder(type))}`}
                isRequired={filterOperation?.needValue}
                errorText={get(errors, ['from', 'message'], '')}
                isErrored={
                  showError &&
                  (get(errors, ['message'], false) ||
                    get(errors, ['from', 'message'], false))
                }
                value={getInputValue(
                  type,
                  get(value, ['value', 'from'], ''),
                  accountInfo,
                )}
                onChange={(date: Date) => {
                  onChange({
                    ...value,
                    name,
                    evaluate_on: EVALUATE_ON,
                    value:
                      typeof value.value === 'object'
                        ? {
                            ...value.value,
                            from: setInputValue(type, date, accountInfo),
                          }
                        : {
                            from: setInputValue(type, date, accountInfo),
                          },
                  });
                }}
              />
            )}
            {FREEMIUM_INPUT_NUMBER_TYPES.includes(type) && (
              <Input
                id={`${id}-value-number-value-from`}
                name={`${name}-input`}
                disabled={disabled}
                type={getHtmlTypeFromFieldType(type)}
                placeholder={`${t(getPlaceHolder(type))}`}
                isRequired={filterOperation?.needValue}
                errorText={get(errors, ['from', 'message'], '')}
                isErrored={
                  showError &&
                  (get(errors, ['message'], false) ||
                    get(errors, ['from', 'message'], false))
                }
                value={
                  getInputValue(
                    filterOperation?.inputValueType,
                    get(value, ['value', 'from'], ''),
                    accountInfo,
                  ) || ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange({
                    ...value,
                    name,
                    evaluate_on: EVALUATE_ON,
                    value:
                      typeof value.value === 'object'
                        ? {
                            ...value.value,
                            from: setInputValue(
                              filterOperation?.inputValueType,
                              e.target.value,
                              accountInfo,
                            ),
                          }
                        : {
                            from: setInputValue(
                              filterOperation?.inputValueType,
                              e.target.value,
                              accountInfo,
                            ),
                          },
                  });
                }}
              />
            )}
          </>
          <div className="condition">and</div>
          <>
            {FREEMIUM_INPUT_DATE_TYPES.includes(type) && (
              <DatePicker
                id={`${id}-value`}
                name={`${name}-input`}
                disabled={disabled}
                placeholder={`${t(getPlaceHolder(type))}`}
                isRequired={filterOperation?.needValue}
                errorText={get(errors, ['to', 'message'], '')}
                isErrored={
                  showError &&
                  (get(errors, ['message'], false) ||
                    get(errors, ['to', 'message'], false))
                }
                value={getInputValue(
                  type,
                  get(value, ['value', 'to'], ''),
                  accountInfo,
                )}
                onChange={(date: Date) => {
                  onChange({
                    ...value,
                    name,
                    evaluate_on: EVALUATE_ON,
                    value:
                      typeof value.value === 'object'
                        ? {
                            ...value.value,
                            to: setInputValue(type, date, accountInfo),
                          }
                        : {
                            to: setInputValue(type, date, accountInfo),
                          },
                  });
                }}
              />
            )}
            {FREEMIUM_INPUT_DATE_TIME_TYPES.includes(type) && (
              <DateTimePicker
                id={`${id}-value`}
                name={`${name}-input`}
                disabled={disabled}
                placeholder={`${t(getPlaceHolder(type))}`}
                isRequired={filterOperation?.needValue}
                errorText={get(errors, ['to', 'message'], '')}
                isErrored={
                  showError &&
                  (get(errors, ['message'], false) ||
                    get(errors, ['to', 'message'], false))
                }
                value={getInputValue(
                  type,
                  get(value, ['value', 'to'], ''),
                  accountInfo,
                )}
                onChange={(date: Date) => {
                  onChange({
                    ...value,
                    name,
                    evaluate_on: EVALUATE_ON,
                    value:
                      typeof value.value === 'object'
                        ? {
                            ...value.value,
                            to: setInputValue(type, date, accountInfo),
                          }
                        : {
                            to: setInputValue(type, date, accountInfo),
                          },
                  });
                }}
              />
            )}
            {FREEMIUM_INPUT_NUMBER_TYPES.includes(type) && (
              <Input
                id={`${id}-value-number-value-to`}
                name={`${name}-input`}
                disabled={disabled}
                type={getHtmlTypeFromFieldType(type)}
                placeholder={`${t(getPlaceHolder(type))}`}
                isRequired={filterOperation?.needValue}
                errorText={get(errors, ['to', 'message'], '')}
                isErrored={
                  showError &&
                  (get(errors, ['message'], false) ||
                    get(errors, ['to', 'message'], false))
                }
                value={
                  getInputValue(
                    filterOperation?.inputValueType,
                    get(value, ['value', 'to'], ''),
                    accountInfo,
                  ) || ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange({
                    ...value,
                    name,
                    evaluate_on: EVALUATE_ON,
                    value:
                      typeof value.value === 'object'
                        ? {
                            ...value.value,
                            to: setInputValue(
                              filterOperation?.inputValueType,
                              e.target.value,
                              accountInfo,
                            ),
                          }
                        : {
                            to: setInputValue(
                              filterOperation?.inputValueType,
                              e.target.value,
                              accountInfo,
                            ),
                          },
                  });
                }}
              />
            )}
          </>
        </>
      )}
    </div>
  ) : null;
};

export default FilterOperation;
